import React, { useMemo } from 'react';
import './GameBoard.css';

const TETRIS_COLS = 6;
const TETRIS_ROWS = 8;
// Rows are bottom (index 0) to top (index 7).
const TETRIS_MAP_BASE1 = [
  [0, 0, 0, 1, 1, 1],
  [2, 0, 4, 3, 3, 1],
  [2, 2, 4, 3, 3, 5],
  [6, 2, 4, 5, 5, 5],
  [6, 6, 4, 7, 9, 8],
  [6, 7, 7, 7, 9, 8],
  [10, 10, 11, 9, 9, 8],
  [10, 10, 11, 11, 11, 8],
];
const TETRIS_MAP_BASE2 = [
  [1, 1, 0, 0, 0, 3],
  [1, 1, 0, 2, 2, 3],
  [5, 4, 4, 4, 2, 3],
  [5, 5, 7, 4, 2, 3],
  [5, 9, 7, 6, 6, 6],
  [9, 9, 7, 8, 8, 6],
  [9, 11, 7, 8, 8, 10],
  [11, 11, 11, 10, 10, 10],
];

const MATRIX_COLS = 18;
const MATRIX_ROWS = 24;
const ANIMATION_DURATION_MS = 500;
const ANIMATION_DELAY_STEP_MS = 100;
const TETRIS_ROW_MS = 30;
const MATRIX_ROW_MS = TETRIS_ROW_MS;

function buildTetrisBlocks(map) {
  const blocks = Array.from({ length: 12 }, () => ({ cells: [] }));
  map.forEach((row, y) => {
    row.forEach((blockId, x) => {
      blocks[blockId].cells.push({ x, y });
    });
  });

  return blocks.map((block, id) => {
    const xs = block.cells.map((cell) => cell.x);
    const ys = block.cells.map((cell) => cell.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const width = maxX - minX + 1;
    const height = maxY - minY + 1;
    const topRow = TETRIS_ROWS - 1 - maxY;
    const leftPct = (minX * 100) / TETRIS_COLS;
    const topPct = (topRow * 100) / TETRIS_ROWS;
    const cells = block.cells.map((cell) => {
      const relLeft = cell.x - minX;
      const relTop = maxY - cell.y;
      return {
        leftPct: (relLeft * 100) / width,
        topPct: (relTop * 100) / height,
      };
    });

    return {
      id,
      cells,
      width,
      height,
      leftPct,
      topPct,
      topRow,
    };
  });
}

function buildTetrisRowFills(map) {
  return map.map((row, rowIndex) => {
    let lastIdx = -1;
    row.forEach((blockId) => {
      if (blockId > lastIdx) lastIdx = blockId;
    });
    return { row: rowIndex, lastIdx };
  });
}

function buildMatrixDrops(cols, rows, rowMs, dropDelayMs) {
  const heights = Array(cols).fill(0);
  const drops = [];
  const total = cols * rows;
  let timeline = 0;

  for (let i = 0; i < total; i++) {
    const available = [];
    for (let c = 0; c < cols; c += 1) {
      if (heights[c] < rows) available.push(c);
    }
    const col = available[Math.floor(Math.random() * available.length)];
    const row = heights[col];
    heights[col] += 1;

    const topRow = (rows - 1 - row);
    const duration = Math.max(1, Math.round((topRow + 1) * rowMs));
    timeline += (i === 0 ? 0 : dropDelayMs);

    drops.push({
      id: i,
      col,
      row,
      topRow,
      delayMs: Math.round(timeline),
      durationMs: Math.round(duration),
    });
  }

  return drops;
}

const TETRIS_BLOCKS_BASE1 = buildTetrisBlocks(TETRIS_MAP_BASE1);
const TETRIS_BLOCKS_BASE2 = buildTetrisBlocks(TETRIS_MAP_BASE2);
const TETRIS_ROW_FILLS_BASE1 = buildTetrisRowFills(TETRIS_MAP_BASE1);
const TETRIS_ROW_FILLS_BASE2 = buildTetrisRowFills(TETRIS_MAP_BASE2);

const getTetrisTotalMs = () =>
  Math.max(
    [TETRIS_BLOCKS_BASE1, TETRIS_BLOCKS_BASE2].reduce((maxTotal, blocks) => {
      const total = blocks.reduce(
        (sum, block) => sum + (block.topRow + 1) * TETRIS_ROW_MS,
        0
      );
      return Math.max(maxTotal, total);
    }, 0),
    0
  );

const getMatrixDropDelayMs = (tetrisTotalMs) => {
  const matrixMaxFallMs = MATRIX_ROWS * MATRIX_ROW_MS;
  return Math.max(
    5,
    Math.round((tetrisTotalMs - matrixMaxFallMs) / (MATRIX_COLS * MATRIX_ROWS - 1))
  );
};

const getMatrixTotalMs = (tetrisTotalMs) => {
  const matrixMaxFallMs = MATRIX_ROWS * MATRIX_ROW_MS;
  const dropDelayMs = getMatrixDropDelayMs(tetrisTotalMs);
  return dropDelayMs * (MATRIX_COLS * MATRIX_ROWS - 1) + matrixMaxFallMs;
};

export const getEndOfGuessDelayMs = (animationMode, colIndex = 4) => {
  const colDelayMs = colIndex * ANIMATION_DELAY_STEP_MS;
  if (animationMode === 'tetris') {
    return colDelayMs + getTetrisTotalMs();
  }
  if (animationMode === 'matrix') {
    return colDelayMs + getMatrixTotalMs(getTetrisTotalMs());
  }
  return colDelayMs + ANIMATION_DURATION_MS;
};

const MatrixLayer = ({ color, seedKey, rowMs, dropDelayMs, colDelayMs, isAnimated }) => {
  const drops = useMemo(
    () => buildMatrixDrops(MATRIX_COLS, MATRIX_ROWS, rowMs, dropDelayMs),
    [rowMs, dropDelayMs]
  );

  const lastDropByRow = Array(MATRIX_ROWS).fill(null);
  drops.forEach((drop, idx) => {
    if (!lastDropByRow[drop.row] || idx > lastDropByRow[drop.row].idx) {
      lastDropByRow[drop.row] = { idx, drop };
    }
  });

  return (
    <>
      {drops.map((drop) => {
        const leftPercent = (drop.col * 100) / MATRIX_COLS;
        const topPercent = (drop.topRow * 100) / MATRIX_ROWS;
        return (
          <div
            key={`${seedKey}-${drop.id}`}
            className={`matrix-pixel ${color} ${isAnimated ? '' : 'static'}`}
            style={{
              '--pixel-left': `${leftPercent}%`,
              '--pixel-target-top': `${topPercent}%`,
              animationDelay: `${drop.delayMs + colDelayMs}ms`,
              animationDuration: `${drop.durationMs}ms`,
            }}
          />
        );
      })}
      {lastDropByRow.map((entry, row) => {
        if (!entry) return null;
        const topPercent = (MATRIX_ROWS - 1 - row) * (100 / MATRIX_ROWS);
        const delayMs = entry.drop.delayMs + entry.drop.durationMs;
        return (
          <div
            key={`matrix-row-${row}`}
            className={`matrix-row-fill ${color} ${isAnimated ? '' : 'static'}`}
            style={{
              '--row-top': `${topPercent}%`,
              animationDelay: `${delayMs + colDelayMs}ms`,
            }}
          />
        );
      })}
    </>
  );
};

const TetrisRowFills = ({ color, rowDelays, rowFills, colDelayMs, isAnimated }) => {
  return rowFills.map(({ row, lastIdx }) => {
    const topPercent = (TETRIS_ROWS - 1 - row) * (100 / TETRIS_ROWS);
    return (
      <div
        key={`row-${row}`}
        className={`tetris-row-fill ${color} ${isAnimated ? '' : 'static'}`}
        style={{
          '--row-top': `${topPercent}%`,
          animationDelay: `${rowDelays[lastIdx] + colDelayMs}ms`,
        }}
      />
    );
  });
};

const GameBoard = ({
  guesses,
  feedback,
  maxGuesses,
  animationMode = 'slide',
  animateRowIndex = null,
  animateOnSubmitOnly = false,
}) => {
  const NUM_ROWS = 6;
  const NUM_COLS = 5;
  const tetrisTotalMs = getTetrisTotalMs();
  const matrixDropDelayMs = getMatrixDropDelayMs(tetrisTotalMs);
  const matrixTotalMs = getMatrixTotalMs(tetrisTotalMs);

  return (
    <div
      className="game-board"
      style={{
        '--anim-duration': `${ANIMATION_DURATION_MS}ms`,
        '--anim-delay-step': `${ANIMATION_DELAY_STEP_MS}ms`,
        '--tetris-total-ms': `${tetrisTotalMs}ms`,
        '--matrix-total-ms': `${matrixTotalMs}ms`
      }}
    >
      {Array.from({ length: NUM_ROWS }).map((_, rowIndex) => {
        const isDisabled = rowIndex >= maxGuesses;
        return (
          <div className={`row ${isDisabled ? 'disabled' : ''}`} key={rowIndex}>
            {Array.from({ length: NUM_COLS }).map((_, colIndex) => {
              const char = guesses[rowIndex]?.[colIndex] || '';
              const color = feedback[rowIndex]?.[colIndex] || 'default';
              const shouldAnimateRow =
                !animateOnSubmitOnly || animateRowIndex === rowIndex;
              const colDelayMs = colIndex * ANIMATION_DELAY_STEP_MS;
              const tetrisVariant = pickTetrisVariant(`${rowIndex}-${colIndex}`);
              let tetrisResolved = TETRIS_BLOCKS_BASE1;
              let tetrisRowFills = TETRIS_ROW_FILLS_BASE1;
              if (tetrisVariant === 'base1x') {
                tetrisResolved = TETRIS_BLOCKS_BASE1X;
              } else if (tetrisVariant === 'base2') {
                tetrisResolved = TETRIS_BLOCKS_BASE2;
                tetrisRowFills = TETRIS_ROW_FILLS_BASE2;
              } else if (tetrisVariant === 'base2x') {
                tetrisResolved = TETRIS_BLOCKS_BASE2X;
                tetrisRowFills = TETRIS_ROW_FILLS_BASE2;
              }
              const tetrisDurations = tetrisResolved.map(
                (block) => (block.topRow + 1) * TETRIS_ROW_MS
              );
              const tetrisStarts = [];
              const tetrisEnds = [];
              let tetrisAcc = 0;
              tetrisDurations.forEach((duration, idx) => {
                tetrisStarts[idx] = tetrisAcc;
                tetrisAcc += duration;
                tetrisEnds[idx] = tetrisAcc;
              });
              return (
                <div
                  className={`cell ${animationMode} ${color} ${isDisabled ? 'disabled' : ''} ${shouldAnimateRow ? '' : 'static'}`}
                  key={`${rowIndex}-${colIndex}`}
                  style={{ '--col-index': colIndex }}
                >
                  <span className="cell-text">{char}</span>
      {animationMode === 'tetris' && color !== 'default' && (
        <>
          <div className="tetris-layer">
            {/* 12 Unique Blocks for Perfect Tiling */}
            {tetrisResolved.map((resolved, i) => {
              const duration = tetrisDurations[i];
              const delay = tetrisStarts[i];
              return (
                <div
                  key={i}
                  className={`tetris-block ${color} ${shouldAnimateRow ? '' : 'static'}`}
                  style={{
                    '--tetris-fall-ms': `${duration}ms`,
                    '--block-delay': `${delay + colDelayMs}ms`,
                    '--target-left': `${resolved.leftPct}%`,
                    '--target-top': `${resolved.topPct}%`,
                    '--block-width': `${(resolved.width * 100) / TETRIS_COLS}%`,
                    '--block-height': `${(resolved.height * 100) / TETRIS_ROWS}%`,
                  }}
                >
                  {resolved.cells.map((cell, cellIndex) => (
                    <div
                      key={`${i}-${cellIndex}`}
                      className="tetris-block-cell"
                      style={{
                        left: `${cell.leftPct}%`,
                        top: `${cell.topPct}%`,
                        width: `${100 / resolved.width}%`,
                        height: `${100 / resolved.height}%`,
                      }}
                    />
                  ))}
                </div>
              );
            })}
            <TetrisRowFills
              color={color}
              rowDelays={tetrisEnds}
              rowFills={tetrisRowFills}
              colDelayMs={colDelayMs}
              isAnimated={shouldAnimateRow}
            />
          </div>
        </>
      )}
                  {animationMode === 'matrix' && color !== 'default' && (
                    <MatrixLayer
                      color={color}
                      seedKey={`${rowIndex}-${colIndex}-${color}`}
                      rowMs={MATRIX_ROW_MS}
                      dropDelayMs={matrixDropDelayMs}
                      colDelayMs={colDelayMs}
                      isAnimated={shouldAnimateRow}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default GameBoard;
function hashSeed(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pickTetrisVariant(seedKey) {
  const v = hashSeed(seedKey) % 4;
  if (v === 1) return 'base1x';
  if (v === 2) return 'base2';
  if (v === 3) return 'base2x';
  return 'base1';
}

function mirrorTetrisMapX(map) {
  return map.map((row) => row.slice().reverse());
}

const TETRIS_MAP_BASE1X = mirrorTetrisMapX(TETRIS_MAP_BASE1);
const TETRIS_MAP_BASE2X = mirrorTetrisMapX(TETRIS_MAP_BASE2);
const TETRIS_BLOCKS_BASE1X = buildTetrisBlocks(TETRIS_MAP_BASE1X);
const TETRIS_BLOCKS_BASE2X = buildTetrisBlocks(TETRIS_MAP_BASE2X);
